/**
 * The agency's own details, turned into the strings a page can print.
 *
 * Pure functions, in a .js module rather than inside the components that
 * use them, for two reasons. They are about the shape of the brand record
 * rather than about any one template, so the next template needs them too;
 * and a test can run them, which a .jsx file cannot be imported to do.
 *
 * Every one of them has to survive a record that has not been filled in. An
 * agency signs up, gets a site the same day, and fills the record in over
 * the following week: "not yet" is the normal state, not the edge case.
 */

/**
 * "Windlebrook Estates" -> ["Windlebrook", "Estates"], for the design's
 * two-line wordmark. A one-word agency gets one line rather than a line and
 * an empty one.
 */
export function wordmarkParts(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [words[0] || "", ""];
  return [words.slice(0, -1).join(" "), words[words.length - 1]];
}

/**
 * The office address as one line.
 *
 * It is an object on the brand record and null on an agency that has not
 * filled it in. Rendering the object put "Objects are not valid as a React
 * child" through the error boundary and took the whole page down, which is
 * how this came to exist.
 *
 * The street number goes in, unlike a property's: this is the agency's own
 * address and the standing rule is about what is for sale.
 */
export function addressLine(address) {
  if (!address) return "";
  if (typeof address === "string") return address.trim();
  return ["street", "town", "county", "postcode"]
    .map((k) => String(address[k] || "").trim())
    .filter(Boolean)
    .join(", ");
}

/** "Monday to Friday, 08:30 to 17:00", from whatever shape is stored. */
export function hoursLine(entry) {
  if (!entry) return "";
  if (typeof entry === "string") return entry.trim();
  const days = Array.isArray(entry.days)
    ? (entry.days.length > 1 ? `${entry.days[0]} to ${entry.days[entry.days.length - 1]}` : entry.days[0])
    : String(entry.days || "").trim();
  const hours = entry.opens && entry.closes
    ? `${entry.opens} to ${entry.closes}`
    : String(entry.hours || "").trim();
  return [days, hours].filter(Boolean).join(", ");
}

/**
 * An area guide's opening, short enough to be a caption.
 *
 * The record holds a guide of several paragraphs and the area rail has room
 * for a line. Trimmed at a word boundary, and always the agency's own words:
 * the whole guide is one click away.
 */
export function areaCaption(guide, limit = 96) {
  const text = String(guide || "").trim();
  if (!text) return "";
  const first = text.split(/(?<=\.)\s/)[0];
  if (first.length <= limit) return first;
  const cut = first.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}…`;
}

/**
 * A Land Registry sold address, as a street.
 *
 * Price Paid data is published openly and carries the house number:
 * "34, NEWARK ROAD", "PEELERS, 6, COCHRANE PLACE", "TUDOR COURT, LONDON
 * ROAD". Foundation's standing rule is to record the full address and
 * display the street, and although a completed sale is public record and
 * the portals do show the number, there is no reason for an agency's own
 * area guide to be the place somebody learns which house it was.
 *
 * The street is the last comma-separated part in every one of these, which
 * is far simpler than re-deriving backend/addressing.py's rules in a second
 * language and exactly right for this one dataset.
 *
 * Title-cased because the source SHOUTS.
 */
export function soldStreet(address) {
  const parts = String(address || "").split(",").map((s) => s.trim()).filter(Boolean);
  const street = parts.length ? parts[parts.length - 1] : "";
  return street.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** "2026-07-23" -> "Jul 26", for a sold-price table. */
export function soldMonth(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "Europe/London" });
}
